import toArray from 'lodash/toArray';
import get from 'lodash/get';
import sum from 'lodash/sum';
import flatten from 'lodash/flatten';
import flattenDeep from 'lodash/flattenDeep';
import every from 'lodash/every';
import max from 'lodash/max';
import filter from 'lodash/filter';

import { caseLogicMsg } from '../validationMessages';

function validateCumulativeTimers(caseConf, effectChains) {
  const nodes = toArray(get(caseConf, 'caseLogicDescription.nodes'));
  const errors = [];
  const warnings = [];

  let maxDuration = 900;
  const { timer } = caseConf;
  if (timer) {
    const hours = parseInt(timer.hours) || 0;
    const minutes = parseInt(timer.minutes) || 0;
    const seconds = parseInt(timer.seconds) || 0;
    maxDuration = hours * 3600 + minutes * 60 + seconds;
  }
  nodes.forEach((node) => {
    // Cumulative timers
    if (!get(node, 'inputs.inputs.connections.length')) {
      const timerCumuls = flattenDeep(
        computeTimerCumuls(node, caseConf.caseLogicDescription, effectChains)
      );
      timerCumuls.forEach((cumul) => {
        if (sum(cumul.timers) <= maxDuration) {
          return;
        }
        cumul.timers.forEach((timer, index) => {
          const nodeId = cumul.path[index];
          const cumulatedTimer = sum(cumul.timers.slice(0, index + 1));
          if (cumulatedTimer > maxDuration) {
            warnings.push(
              caseLogicMsg(
                'This node will not execute because the path it is on exceeds the duration of the case',
                nodeId,
                {
                  nodeId: nodeId,
                  scope: 'node',
                  defaultVisibility: false,
                }
              )
            );
          }
        });
      });
    }
    // Timer duration of 0
    if (node.name === 'Timer' && get(node, 'inputs.inputs.connections.length')) {
      if (!node.data.expiry || node.data.expiry < 1) {
        errors.push(caseLogicMsg('Timers with inputs must be at least 1 second duration', node.id));
      }
    }
  });
  return {
    errors,
    warnings,
  };
}

/**
 * Returns an array of cumuls starting from a node in a given case logic
 *
 * @param {CLDNode} node - the node to start computing timer cumuls for
 * @param {CLD} caseLogicDescription - the case logic description
 * @param {Array<CLD>} effectChains - the referenced effect chains in the CLD
 * @param {Array<Cumul>} cumul - an array of cumuls, one for each path coming out of the node, each cumul object has a path (node indices), and timers (node timers)
 */
function computeTimerCumuls(
  node,
  caseLogicDescription,
  effectChains,
  cumul = { path: [], timers: [] }
) {
  const outputConnections = flatten([
    get(node, 'outputs.outputs.connections'),
    get(node, 'outputs.targets.connections'),
    get(node, 'outputs.trueOutputs.connections'),
    get(node, 'outputs.falseOutputs.connections'),
  ]).filter((v) => !!v);
  if (!outputConnections.length) {
    return {
      path: cumul.path.concat(node.id),
      timers: cumul.timers.concat(getNodeTimer(node, effectChains)),
    };
  }
  return outputConnections.map((connection) => {
    const outputNode = caseLogicDescription.nodes[connection.node];
    if (!outputNode) {
      return cumul;
    }
    // Prevent case logic loops from causing infinite recursion
    // When a loop is encountered, stop the path at the loop
    if (cumul.path.includes(outputNode.id)) {
      return cumul;
    }
    return computeTimerCumuls(outputNode, caseLogicDescription, effectChains, {
      path: cumul.path.concat(node.id),
      timers: cumul.timers.concat(getNodeTimer(node, effectChains)),
    });
  });
}

/**
 * Returns the maximum amount of time a given node will execute in
 *
 * For timers, returns the timer value. For effects, returns the effect duration
 * and for effect chains returns the time the longest path in the effect chains takes
 * to execute
 *
 * @param {CLDNode} node - case logic description node
 * @param {Array<CLD>} referencedEffectChains - referenced effect chains inside thhe CLD
 */
const getNodeTimer = (node, referencedEffectChains) => {
  if (node.name === 'EffectChain') {
    const type = get(node, 'data.effectChain.type');
    let effectChain;
    if (type === 'EXISTING_EFFECT_CHAIN') {
      const effectChainId = get(node, 'data.effectChain.id');
      effectChain = referencedEffectChains.find((ec) => ec.id === effectChainId);
    } else {
      effectChain = node.data.effectChain;
    }
    if (!effectChain) {
      return [];
    }
    const { nodes } = effectChain;
    const startNodes = filter(nodes, (node) =>
      every(node.inputs, (inputGroup) => inputGroup.connections.length === 0)
    );
    const ecTimerCumuls = flattenDeep(startNodes.map((node) =>
      computeTimerCumuls(node, effectChain, referencedEffectChains)
    ));
    return max(ecTimerCumuls.map(cumul => sum(cumul.timers)));
  }
  return get(node, 'data.expiry') || get(node, 'data.effect.duration') || 0;
};

export default validateCumulativeTimers;
